import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import Autocomplete from '@mui/material/Autocomplete'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import type { SxProps, Theme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useRef, useState, type Ref } from 'react'
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form'

export type Option = { id: string; label: string; hint?: string }

type Common<T extends FieldValues> = {
  /** `any` for the context and transformed-value generics: these fields are used with
   *  forms whose zod output type differs from their input type. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<T, any, any>
  name: Path<T>
  label: string
  disabled?: boolean
  helperText?: string
}

/** Searchable single-select bound to a string id; `''` means "no selection". */
export function EntitySelect<T extends FieldValues>({
  control,
  name,
  label,
  options,
  loading,
  disabled,
  helperText,
  emptyLabel = 'ללא',
}: Common<T> & { options: Option[]; loading?: boolean; emptyLabel?: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selected = options.find((o) => o.id === field.value) ?? null
        return (
          <Autocomplete
            options={options}
            value={selected}
            loading={loading}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_event, option) => field.onChange(option?.id ?? '')}
            noOptionsText="אין תוצאות"
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={emptyLabel}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? helperText}
              />
            )}
          />
        )
      }}
    />
  )
}

/** Fixed-choice select for enums. */
export function EnumSelect<T extends FieldValues>({
  control,
  name,
  label,
  options,
  disabled,
  helperText,
  allowEmpty,
  emptyLabel = 'הכל',
}: Common<T> & {
  options: Option[]
  allowEmpty?: boolean
  emptyLabel?: string
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          select
          label={label}
          disabled={disabled}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? helperText}
        >
          {allowEmpty ? <MenuItem value="">{emptyLabel}</MenuItem> : null}
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  )
}

/**
 * Typed parsing for a calendar day. Day comes first, as people write dates here:
 * "5/11/26", "05.11.2026", "5-11-2026", "051126" and "05112026" all mean 5 November 2026.
 * Returns `YYYY-MM-DD`, `''` for an empty input, or `null` when it is not a real date.
 */
export function parseTypedDate(text: string): string | null {
  const trimmed = text.trim()
  if (!trimmed) return ''

  let parts: string[] | null = null
  const separated = /^(\d{1,2})\s*[/.\-\s]\s*(\d{1,2})\s*[/.\-\s]\s*(\d{2}|\d{4})$/.exec(trimmed)
  if (separated) parts = separated.slice(1)
  else if (/^\d{6}$|^\d{8}$/.test(trimmed)) {
    parts = [trimmed.slice(0, 2), trimmed.slice(2, 4), trimmed.slice(4)]
  }
  if (!parts) return null

  const [d = '', m = '', y = ''] = parts
  const day = Number(d)
  const month = Number(m)
  const year = y.length === 2 ? 2000 + Number(y) : Number(y)
  const parsed = dayjs(new Date(year, month - 1, day))
  if (parsed.date() !== day || parsed.month() !== month - 1 || year < 1900 || year > 2200) return null
  return parsed.format('YYYY-MM-DD')
}

/** A typed value complete enough to apply while the user is still in the field. */
const isCompleteTyping = (text: string) => /\d{4}$|^\d{8}$/.test(text.trim())

const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY'

const toDisplay = (value: string | null | undefined) =>
  value ? dayjs(value).format(DATE_DISPLAY_FORMAT) : ''

type DateTextFieldProps = {
  label?: string
  /** `''` or `YYYY-MM-DD`. */
  value: string
  onChange: (next: string) => void
  onBlur?: () => void
  disabled?: boolean
  helperText?: string
  error?: boolean
  autoFocus?: boolean
  inputRef?: Ref<HTMLInputElement>
  variant?: 'outlined' | 'standard'
  size?: 'small' | 'medium'
  sx?: SxProps<Theme>
}

/**
 * A date the user can simply type, with a calendar button beside it. Always `DD/MM/YYYY`
 * on screen — the native date input follows the browser's locale, and the MUI picker's
 * sectioned field fights free typing in a right-to-left layout.
 */
export function DateTextField({
  label,
  value,
  onChange,
  onBlur,
  disabled,
  helperText,
  error,
  autoFocus,
  inputRef,
  variant = 'outlined',
  size,
  sx,
}: DateTextFieldProps) {
  const [text, setText] = useState(() => toDisplay(value))
  const [invalid, setInvalid] = useState(false)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const focused = useRef(false)

  // An outside change (form reset, calendar pick, computed default) replaces the text —
  // but not while the user is typing the very date it now holds.
  useEffect(() => {
    if (focused.current && parseTypedDate(text) === value) return
    setText(toDisplay(value))
    setInvalid(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const apply = (next: string) => {
    setInvalid(false)
    if (next !== value) onChange(next)
  }

  const commit = () => {
    const parsed = parseTypedDate(text)
    if (parsed === null) {
      setInvalid(true)
      return
    }
    setText(toDisplay(parsed))
    apply(parsed)
  }

  return (
    <>
      <TextField
        label={label}
        value={text}
        disabled={disabled}
        autoFocus={autoFocus}
        inputRef={inputRef}
        variant={variant}
        size={size}
        fullWidth
        sx={sx}
        placeholder="DD/MM/YYYY"
        error={error || invalid}
        helperText={invalid ? 'תאריך לא תקין — לדוגמה 05/11/2026' : helperText}
        onChange={(e) => {
          const next = e.target.value
          setText(next)
          // A complete date takes effect at once, so saving straight from the field works.
          const parsed = parseTypedDate(next)
          if (parsed === '' || (parsed !== null && isCompleteTyping(next))) apply(parsed)
        }}
        onFocus={() => {
          focused.current = true
        }}
        onBlur={() => {
          focused.current = false
          commit()
          onBlur?.()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
        }}
        slotProps={{
          htmlInput: { dir: 'ltr', style: { textAlign: 'end' }, inputMode: 'numeric' },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  disabled={disabled}
                  aria-label="בחירה מלוח שנה"
                  onClick={(e) =>
                    setAnchor(e.currentTarget.closest<HTMLElement>('.MuiFormControl-root'))
                  }
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <DateCalendar
          value={value ? dayjs(value) : null}
          onChange={(next: Dayjs | null) => {
            const day = next && next.isValid() ? next.format('YYYY-MM-DD') : ''
            setAnchor(null)
            setText(toDisplay(day))
            apply(day)
          }}
        />
      </Popover>
    </>
  )
}

/** Calendar day bound to the `YYYY-MM-DD` string the API expects. */
export function DateField<T extends FieldValues>({
  control,
  name,
  label,
  disabled,
  helperText,
}: Common<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <DateTextField
          label={label}
          disabled={disabled}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? helperText}
        />
      )}
    />
  )
}

/** The same field for plain state: `value` is `''` or `YYYY-MM-DD`. */
export function DateInput({
  label,
  value,
  onChange,
  disabled,
  helperText,
  size,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  helperText?: string
  size?: 'small' | 'medium'
}) {
  return (
    <DateTextField
      label={label}
      value={value}
      onChange={onChange}
      disabled={disabled}
      helperText={helperText}
      size={size}
    />
  )
}

